import { createGlobalStyle } from "styled-components";
import { TextAlignment } from "./TextProps";
import { Body1Styles, Body2Styles, Body3Styles } from "./body";

export default createGlobalStyle`

// This is the site-wide root CSS where we define our global variables
:root {
  --font-elan: "elan-itc-pro", sans-serif;
  --font-helvetica: "helvetica-now-display", sans-serif;
  --font-urw:  "helvetica-now-display", sans-serif;
  --focus-visible: 0.2rem solid #5b9dd9;

  /**
   * Colours
   */
  --colour-black: #1A1A1A;
  --colour-grey-dark: #333333;
  --colour-grey-medium: #666666;
  --colour-grey-inactive: #b2b2b2;
  --colour-grey-pinline: #cccccc;
  --colour-grey-background: #e9e9e9;
  --colour-grey-item-background: #fafafa;
  --colour-black-gradient: #646464;
  --colour-white: #ffffff;
    
  /*
   * Feedback Colours
   */
  --colour-red-3: #990000;
  --colour-red-2: #c86464;
  --colour-red-1: #fbebeb;

  --colour-yellow-3: #785e1c;
  --colour-yellow-2: #ab934f;
  --colour-yellow-1: #f8f2d5;

  --colour-blue-3: #31668f;
  --colour-blue-2: #5b9dd9;
  --colour-blue-1: #e3edf2;

  --colour-green-3: #227733;
  --colour-green-2: #5aab6a;
  --colour-green-1: #dff0d8;
  
  --gradient-active: linear-gradient(180deg, var(--colour-black) 9.38%, var(--colour-black-gradient)  73.96%), var(--colour-white);
  --gradient-hover: linear-gradient(180deg, var(--colour-black-gradient) 5.21%, var(--colour-black) 87.5%), var(--colour-white);
  
  /* Why Renaming Base Color Variables? */
  --colour-copy: var(--colour-black);
  --black: var(--colour-black);
  --dark-grey: var(--colour-grey-dark);
  --medium-grey: var(--colour-grey-medium);
  --inactive-state-grey: var(--colour-grey-inactive);
  --pinline-grey: var(--colour-grey-pinline);
  --background-grey: var(--colour-grey-background);
  --background-gradient: var(--colour-black-gradient);
  --white: var(--colour-white);

  /* WHY Renaming Color Variables in Core-UI? */
  --warning-red: var(--colour-red-3);
  --medium-red: var(--colour-red-2);
  --background-red: var(--colour-red-1);

  /* WHY Renaming Color Variables in Core-UI? */
  --caution-yellow: var(--colour-yellow-3);
  --medium-yellow: var(--colour-yellow-2);
  --background-yellow: var(--colour-yellow-1);

  /* WHY Renaming Color Variables in Core-UI? */
  --info-blue: var(--colour-blue-3);
  --focus-blue: var(--colour-blue-2);
  --background-blue: var(--colour-blue-1);

  /* WHY Renaming Color Variables in Core-UI? */
  --success-green: var(--colour-green-3);
  --medium-green: var(--colour-green-2);
  --background-green: var(--colour-green-1);

  /**
   * Spacing Units (Deprecated)
   * These tie in directly with the coloured spaces depicted in design mocks
   * Responsiveness is built-in to the variable, so no component-level tweaks needed
   */
  --space-red: 6rem;
  --space-pink: 4rem;
  --space-purple: 2rem;
  --space-green: 2rem;
  --space-blue: 1rem;
  --space-orange: .5rem;

  @media(min-width: 1024px) {
    --space-purple: 3rem;
    --space-pink: 6rem;
    --space-red: 10rem;
  }

  /**
   * Spacing Units
   * These tie in directly with the spacing units defined in Figma
   * https://www.figma.com/file/xiTC7i82LbrfGrMQRqsxLY/Arc'teryx-Components?type=design&node-id=781-52&mode=design&t=LHI4MFqa1SCYqNKI-0
   */
  --desk-mobile05: 0.5rem;
  --desk-mobile1: 1rem;
  --desk-mobile2: 2rem;
  --desk3-mobile2: 2rem;
  --desk6-mobile4: 4rem;
  --desk10-mobile6: 6rem;

  @media(min-width: 992px) {
    --desk3-mobile2: 3rem;
    --desk6-mobile4: 6rem;
    --desk10-mobile6: 10rem;
  }

  // Spacing Units - Aliases
  --space-xs: var(--desk-mobile05);
  --space-s: var(--desk-mobile1);
  --space-m: var(--desk-mobile2);
  --space-l: var(--desk3-mobile2);
  --space-xl: var(--desk6-mobile4);
  --space-xxl: var(--desk10-mobile6);

  /**
   * CatIntro Banners
   */
   --banner-height-xs: 10rem; /* 150px */
   --banner-height-sm: 12rem; /* 180px */
   --banner-height-md: 13.333rem; /* 200px */
   --banner-height-lg: 16.666rem; /* 250px */
   --banner-height-xl: 23.333rem; /* 350px */

   /**
    * Common breakpoints
    */
   --breakpoint-sm: 576px;
   --breakpoint-md: 768px;
   --breakpoint-lg: 992px;
   --breakpoint-xl: 1200px;
   /**
    * ProductTiles
    */
   --product-tile-2-col: 50%;
   --product-tile-3-col: 33.33%;
   --product-tile-4-col: 25%;
}

html {
  height: 100%;
  height: -webkit-fill-available;
  scroll-behavior: smooth;

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
}

ul[class],
ol[class] {
  padding: 0;
}

body,
ul[class],
ol[class],
li,
figure,
figcaption,
blockquote,
dl,
dd {
  margin: 0;
}
body {
  min-height: 100vh;
  min-height: -webkit-fill-available;
  text-rendering: optimizeSpeed;
  font-size: 1rem;
  line-height: 1.5;
  font-family: var(--font-helvetica);
  color: var(--colour-black);
}

ol, ul {
  list-style-position: outside;
  margin: 1rem 0 0 0;
  padding: 0 0 0 2rem;
}
ul {
  list-style-type: disc;
}
li {
  line-height: 1.5rem;
  padding-bottom: 0.5rem;
}

ul[class],
ol[class] {
  list-style: none;
}

/* helper class to hide scrollbars */
.hide--scrollbar {
  /* Chrome, Safari and Opera */
  ::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;  /* Firefox */
}

a:not([class]) {
  text-decoration: underline;
  text-decoration-skip-ink: auto;
}

a:focus, button:focus {
  outline: none;
}

a:focus-visible, button:focus-visible {
  outline: var(--focus-visible);
}

a:-moz-focus-visible, button:-moz-focus-visible,
:-moz-focusring {
  outline: var(--focus-visible);
}

/**
  *  Heading tags; h1, h2, h3, h4
  *  Ideally all Heading tags will use the <H1>, <H2>, <H3>, and <H4> provided by @arcteryx/components-typography
  *  but this will help catch any tags that might not be styled-components
  */
h1, h2, h3, h4 {
  font-weight: 700;
  line-height: 1.5rem;
  margin-top: 0;
}

h1, h4 {
    font-family: var(--font-elan);
}
h2, h3, h5, h6 {
    font-family: var(--font-helvetica);
}

h1 {
  margin-bottom: 1rem;
  font-size: 1.5rem;
}
h2 {
  margin-bottom: 1rem;
  font-size: 1.25rem;
}
h3 {
  margin-bottom: 0.5rem;
  font-size: 1.1rem;
}
h4 {
  margin-bottom: 0.5rem;
  font-size: 1rem;
}

p {
  ${Body1Styles({ alignment: TextAlignment.INHERIT })};

  strong, b {
    ${Body3Styles({ alignment: TextAlignment.INHERIT })}
  }

  em, i {
    ${Body2Styles({ alignment: TextAlignment.INHERIT })}
  }

  &:last-of-type {
    margin-bottom: 0;
  }
}

strong, b {
  ${Body3Styles({ alignment: TextAlignment.INHERIT })}
}

em, i {
  ${Body2Styles({ alignment: TextAlignment.INHERIT })}
}

img {
  max-width: 100%;
  display: block;
}

.lazyload,
.lazyloading {
  opacity: 0;
}

.lazyloaded {
  opacity: 1;
  transition: opacity 200ms;
}

/* Legacy class for header nav hover overlay */

.overlay-dark--full {
  position: fixed;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  opacity: 1;
  z-index: 250;
}

/**
 * a11y
 * For folks who's browser requests reduced motion
 */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
}

/* Helvetica Now Display */
@font-face {
  /* Light */
  font-family: "helvetica-now-display";
  font-style: normal;
  font-stretch: normal;
  font-display: swap;
  font-weight: 300;
  src: url("https://images.arcteryx.com/fonts/HelveticaNowDisplayLight_normal_normal.woff2") format("woff2"),
  url("https://images.arcteryx.com/fonts/HelveticaNowDisplayLight_normal_normal.woff") format("woff");
}

@font-face {
  /* Italic */
  font-family: "helvetica-now-display";
  font-style: italic;
  font-stretch: normal;
  font-display: swap;
  font-weight: 400;
  src: url("https://images.arcteryx.com/fonts/HelveticaNowDisplayItalic_italic_normal.woff2") format("woff2"),
  url("https://images.arcteryx.com/fonts/HelveticaNowDisplayItalic_italic_normal.woff") format("woff");
}

@font-face {
  /* Regular */
  font-family: "helvetica-now-display";
  font-style: normal;
  font-stretch: normal;
  font-display: swap;
  font-weight: 400;
  src: url("https://images.arcteryx.com/fonts/HelveticaNowDisplayRegular_normal_normal.woff2") format("woff2"),
  url("https://images.arcteryx.com/fonts/HelveticaNowDisplayRegular_normal_normal.woff") format("woff");
}

@font-face {
  /* Bold */
  font-family: "helvetica-now-display";
  font-style: normal;
  font-stretch: normal;
  font-display: swap;
  font-weight: 700;
  src: url("https://images.arcteryx.com/fonts/HelveticaNowDisplayBold_normal_normal.woff2") format("woff2"),
  url("https://images.arcteryx.com/fonts/HelveticaNowDisplayBold_normal_normal.woff") format("woff");
}

/* Elan ITC */
@font-face {
    font-family: "elan-itc-pro";
    font-style: normal;
    font-stretch: normal;
    font-display: swap;
    font-weight: 400;
    src:
      url('https://images.arcteryx.com/fonts/ElanITCPro-Book-Web.woff2') format('woff2'),
      url('https://images.arcteryx.com/fonts/ElanITCPro-Book-Web.woff') format('woff');
}

/*
 * The Typekit service used to deliver this font or fonts for use on websites
 * is provided by Adobe and is subject to these Terms of Use
 * http://www.adobe.com/products/eulas/tou_typekit. For font license
 * information, see the list below.
 *
 * urw-din:
 *   - http://typekit.com/eulas/00000000000000007735a839
 *   - http://typekit.com/eulas/00000000000000007735a83d
 *   - http://typekit.com/eulas/00000000000000007735a840
 *   - http://typekit.com/eulas/00000000000000007735a843
 *   - http://typekit.com/eulas/00000000000000007735a846
 *   - http://typekit.com/eulas/00000000000000007735a849
 *   - http://typekit.com/eulas/00000000000000007735a84b
 *   - http://typekit.com/eulas/00000000000000007735a84f
 *   - http://typekit.com/eulas/00000000000000007735a851
 *   - http://typekit.com/eulas/00000000000000007735a855
 * urw-din-condensed:
 *   - http://typekit.com/eulas/00000000000000007735a83b
 *   - http://typekit.com/eulas/00000000000000007735a83e
 *   - http://typekit.com/eulas/00000000000000007735a841
 *   - http://typekit.com/eulas/00000000000000007735a844
 *   - http://typekit.com/eulas/00000000000000007735a847
 *   - http://typekit.com/eulas/00000000000000007735a84a
 *   - http://typekit.com/eulas/00000000000000007735a84d
 *   - http://typekit.com/eulas/00000000000000007735a850
 *   - http://typekit.com/eulas/00000000000000007735a853
 *   - http://typekit.com/eulas/00000000000000007735a856
 * urw-din-semi-condensed:
 *   - http://typekit.com/eulas/00000000000000007735a83c
 *   - http://typekit.com/eulas/00000000000000007735a83f
 *   - http://typekit.com/eulas/00000000000000007735a842
 *   - http://typekit.com/eulas/00000000000000007735a845
 *   - http://typekit.com/eulas/00000000000000007735a848
 *   - http://typekit.com/eulas/00000000000000007735a84c
 *   - http://typekit.com/eulas/00000000000000007735a84e
 *   - http://typekit.com/eulas/00000000000000007735a852
 *   - http://typekit.com/eulas/00000000000000007735a854
 *   - http://typekit.com/eulas/00000000000000007735a857
 *
 * © 2009-2023 Adobe Systems Incorporated. All Rights Reserved.
 */
/*{"last_published":"2023-09-27 18:05:37 UTC"}*/

@import url("https://p.typekit.net/p.css?s=1&k=amb0fbb&ht=tk&f=36601.36602.36603.36604.36605.36606.36607.36608.36609.36610.36617.36618.36619.36620.36621.36622.36623.36624.36625.36626.36633.36634.36635.36636.36637.36638.36639.36640.36641.36642&a=131936418&app=typekit&e=css");

@font-face {
font-family:"urw-din";
src:url("https://use.typekit.net/af/1f0265/00000000000000007735a839/30/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i4&v=3") format("woff2"),url("https://use.typekit.net/af/1f0265/00000000000000007735a839/30/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i4&v=3") format("woff"),url("https://use.typekit.net/af/1f0265/00000000000000007735a839/30/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i4&v=3") format("opentype");
font-display:auto;font-style:italic;font-weight:400;font-stretch:normal;
}

@font-face {
font-family:"urw-din";
src:url("https://use.typekit.net/af/f1d51b/00000000000000007735a83d/30/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n4&v=3") format("woff2"),url("https://use.typekit.net/af/f1d51b/00000000000000007735a83d/30/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n4&v=3") format("woff"),url("https://use.typekit.net/af/f1d51b/00000000000000007735a83d/30/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n4&v=3") format("opentype");
font-display:auto;font-style:normal;font-weight:400;font-stretch:normal;
}

@font-face {
font-family:"urw-din";
src:url("https://use.typekit.net/af/5f44f1/00000000000000007735a840/30/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i5&v=3") format("woff2"),url("https://use.typekit.net/af/5f44f1/00000000000000007735a840/30/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i5&v=3") format("woff"),url("https://use.typekit.net/af/5f44f1/00000000000000007735a840/30/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i5&v=3") format("opentype");
font-display:auto;font-style:italic;font-weight:500;font-stretch:normal;
}

@font-face {
font-family:"urw-din";
src:url("https://use.typekit.net/af/d96df7/00000000000000007735a843/30/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n5&v=3") format("woff2"),url("https://use.typekit.net/af/d96df7/00000000000000007735a843/30/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n5&v=3") format("woff"),url("https://use.typekit.net/af/d96df7/00000000000000007735a843/30/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n5&v=3") format("opentype");
font-display:auto;font-style:normal;font-weight:500;font-stretch:normal;
}

@font-face {
font-family:"urw-din";
src:url("https://use.typekit.net/af/caf48f/00000000000000007735a846/30/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i6&v=3") format("woff2"),url("https://use.typekit.net/af/caf48f/00000000000000007735a846/30/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i6&v=3") format("woff"),url("https://use.typekit.net/af/caf48f/00000000000000007735a846/30/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i6&v=3") format("opentype");
font-display:auto;font-style:italic;font-weight:600;font-stretch:normal;
}

@font-face {
font-family:"urw-din";
src:url("https://use.typekit.net/af/b8eeae/00000000000000007735a849/30/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n6&v=3") format("woff2"),url("https://use.typekit.net/af/b8eeae/00000000000000007735a849/30/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n6&v=3") format("woff"),url("https://use.typekit.net/af/b8eeae/00000000000000007735a849/30/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n6&v=3") format("opentype");
font-display:auto;font-style:normal;font-weight:600;font-stretch:normal;
}

@font-face {
font-family:"urw-din";
src:url("https://use.typekit.net/af/1c5092/00000000000000007735a84b/30/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i7&v=3") format("woff2"),url("https://use.typekit.net/af/1c5092/00000000000000007735a84b/30/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i7&v=3") format("woff"),url("https://use.typekit.net/af/1c5092/00000000000000007735a84b/30/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i7&v=3") format("opentype");
font-display:auto;font-style:italic;font-weight:700;font-stretch:normal;
}

@font-face {
font-family:"urw-din";
src:url("https://use.typekit.net/af/cd97e9/00000000000000007735a84f/30/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n7&v=3") format("woff2"),url("https://use.typekit.net/af/cd97e9/00000000000000007735a84f/30/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n7&v=3") format("woff"),url("https://use.typekit.net/af/cd97e9/00000000000000007735a84f/30/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n7&v=3") format("opentype");
font-display:auto;font-style:normal;font-weight:700;font-stretch:normal;
}

@font-face {
font-family:"urw-din";
src:url("https://use.typekit.net/af/fe2f02/00000000000000007735a851/30/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i9&v=3") format("woff2"),url("https://use.typekit.net/af/fe2f02/00000000000000007735a851/30/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i9&v=3") format("woff"),url("https://use.typekit.net/af/fe2f02/00000000000000007735a851/30/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i9&v=3") format("opentype");
font-display:auto;font-style:italic;font-weight:900;font-stretch:normal;
}

@font-face {
font-family:"urw-din";
src:url("https://use.typekit.net/af/0fba77/00000000000000007735a855/30/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n9&v=3") format("woff2"),url("https://use.typekit.net/af/0fba77/00000000000000007735a855/30/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n9&v=3") format("woff"),url("https://use.typekit.net/af/0fba77/00000000000000007735a855/30/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n9&v=3") format("opentype");
font-display:auto;font-style:normal;font-weight:900;font-stretch:normal;
}

@font-face {
font-family:"urw-din-condensed";
src:url("https://use.typekit.net/af/b27e1e/00000000000000007735a83b/30/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i4&v=3") format("woff2"),url("https://use.typekit.net/af/b27e1e/00000000000000007735a83b/30/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i4&v=3") format("woff"),url("https://use.typekit.net/af/b27e1e/00000000000000007735a83b/30/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i4&v=3") format("opentype");
font-display:auto;font-style:italic;font-weight:400;font-stretch:normal;
}

@font-face {
font-family:"urw-din-condensed";
src:url("https://use.typekit.net/af/ba5c17/00000000000000007735a83e/30/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n4&v=3") format("woff2"),url("https://use.typekit.net/af/ba5c17/00000000000000007735a83e/30/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n4&v=3") format("woff"),url("https://use.typekit.net/af/ba5c17/00000000000000007735a83e/30/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n4&v=3") format("opentype");
font-display:auto;font-style:normal;font-weight:400;font-stretch:normal;
}

@font-face {
font-family:"urw-din-condensed";
src:url("https://use.typekit.net/af/06eb22/00000000000000007735a841/30/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n5&v=3") format("woff2"),url("https://use.typekit.net/af/06eb22/00000000000000007735a841/30/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n5&v=3") format("woff"),url("https://use.typekit.net/af/06eb22/00000000000000007735a841/30/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n5&v=3") format("opentype");
font-display:auto;font-style:normal;font-weight:500;font-stretch:normal;
}

@font-face {
font-family:"urw-din-condensed";
src:url("https://use.typekit.net/af/413a03/00000000000000007735a844/30/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i5&v=3") format("woff2"),url("https://use.typekit.net/af/413a03/00000000000000007735a844/30/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i5&v=3") format("woff"),url("https://use.typekit.net/af/413a03/00000000000000007735a844/30/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i5&v=3") format("opentype");
font-display:auto;font-style:italic;font-weight:500;font-stretch:normal;
}

@font-face {
font-family:"urw-din-condensed";
src:url("https://use.typekit.net/af/c2cf88/00000000000000007735a847/30/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n6&v=3") format("woff2"),url("https://use.typekit.net/af/c2cf88/00000000000000007735a847/30/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n6&v=3") format("woff"),url("https://use.typekit.net/af/c2cf88/00000000000000007735a847/30/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n6&v=3") format("opentype");
font-display:auto;font-style:normal;font-weight:600;font-stretch:normal;
}

@font-face {
font-family:"urw-din-condensed";
src:url("https://use.typekit.net/af/b1d7bd/00000000000000007735a84a/30/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i6&v=3") format("woff2"),url("https://use.typekit.net/af/b1d7bd/00000000000000007735a84a/30/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i6&v=3") format("woff"),url("https://use.typekit.net/af/b1d7bd/00000000000000007735a84a/30/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i6&v=3") format("opentype");
font-display:auto;font-style:italic;font-weight:600;font-stretch:normal;
}

@font-face {
font-family:"urw-din-condensed";
src:url("https://use.typekit.net/af/be96d8/00000000000000007735a84d/30/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n7&v=3") format("woff2"),url("https://use.typekit.net/af/be96d8/00000000000000007735a84d/30/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n7&v=3") format("woff"),url("https://use.typekit.net/af/be96d8/00000000000000007735a84d/30/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n7&v=3") format("opentype");
font-display:auto;font-style:normal;font-weight:700;font-stretch:normal;
}

@font-face {
font-family:"urw-din-condensed";
src:url("https://use.typekit.net/af/3f20e7/00000000000000007735a850/30/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i7&v=3") format("woff2"),url("https://use.typekit.net/af/3f20e7/00000000000000007735a850/30/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i7&v=3") format("woff"),url("https://use.typekit.net/af/3f20e7/00000000000000007735a850/30/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i7&v=3") format("opentype");
font-display:auto;font-style:italic;font-weight:700;font-stretch:normal;
}

@font-face {
font-family:"urw-din-condensed";
src:url("https://use.typekit.net/af/5002a8/00000000000000007735a853/30/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i9&v=3") format("woff2"),url("https://use.typekit.net/af/5002a8/00000000000000007735a853/30/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i9&v=3") format("woff"),url("https://use.typekit.net/af/5002a8/00000000000000007735a853/30/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i9&v=3") format("opentype");
font-display:auto;font-style:italic;font-weight:900;font-stretch:normal;
}

@font-face {
font-family:"urw-din-condensed";
src:url("https://use.typekit.net/af/c85988/00000000000000007735a856/30/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n9&v=3") format("woff2"),url("https://use.typekit.net/af/c85988/00000000000000007735a856/30/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n9&v=3") format("woff"),url("https://use.typekit.net/af/c85988/00000000000000007735a856/30/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n9&v=3") format("opentype");
font-display:auto;font-style:normal;font-weight:900;font-stretch:normal;
}

@font-face {
font-family:"urw-din-semi-condensed";
src:url("https://use.typekit.net/af/68a396/00000000000000007735a83c/30/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n4&v=3") format("woff2"),url("https://use.typekit.net/af/68a396/00000000000000007735a83c/30/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n4&v=3") format("woff"),url("https://use.typekit.net/af/68a396/00000000000000007735a83c/30/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n4&v=3") format("opentype");
font-display:auto;font-style:normal;font-weight:400;font-stretch:normal;
}

@font-face {
font-family:"urw-din-semi-condensed";
src:url("https://use.typekit.net/af/960873/00000000000000007735a83f/30/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i4&v=3") format("woff2"),url("https://use.typekit.net/af/960873/00000000000000007735a83f/30/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i4&v=3") format("woff"),url("https://use.typekit.net/af/960873/00000000000000007735a83f/30/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i4&v=3") format("opentype");
font-display:auto;font-style:italic;font-weight:400;font-stretch:normal;
}

@font-face {
font-family:"urw-din-semi-condensed";
src:url("https://use.typekit.net/af/40dae5/00000000000000007735a842/30/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i5&v=3") format("woff2"),url("https://use.typekit.net/af/40dae5/00000000000000007735a842/30/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i5&v=3") format("woff"),url("https://use.typekit.net/af/40dae5/00000000000000007735a842/30/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i5&v=3") format("opentype");
font-display:auto;font-style:italic;font-weight:500;font-stretch:normal;
}

@font-face {
font-family:"urw-din-semi-condensed";
src:url("https://use.typekit.net/af/5788bc/00000000000000007735a845/30/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n5&v=3") format("woff2"),url("https://use.typekit.net/af/5788bc/00000000000000007735a845/30/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n5&v=3") format("woff"),url("https://use.typekit.net/af/5788bc/00000000000000007735a845/30/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n5&v=3") format("opentype");
font-display:auto;font-style:normal;font-weight:500;font-stretch:normal;
}

@font-face {
font-family:"urw-din-semi-condensed";
src:url("https://use.typekit.net/af/96cf20/00000000000000007735a848/30/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i6&v=3") format("woff2"),url("https://use.typekit.net/af/96cf20/00000000000000007735a848/30/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i6&v=3") format("woff"),url("https://use.typekit.net/af/96cf20/00000000000000007735a848/30/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i6&v=3") format("opentype");
font-display:auto;font-style:italic;font-weight:600;font-stretch:normal;
}

@font-face {
font-family:"urw-din-semi-condensed";
src:url("https://use.typekit.net/af/bd0e8a/00000000000000007735a84c/30/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n6&v=3") format("woff2"),url("https://use.typekit.net/af/bd0e8a/00000000000000007735a84c/30/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n6&v=3") format("woff"),url("https://use.typekit.net/af/bd0e8a/00000000000000007735a84c/30/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n6&v=3") format("opentype");
font-display:auto;font-style:normal;font-weight:600;font-stretch:normal;
}

@font-face {
font-family:"urw-din-semi-condensed";
src:url("https://use.typekit.net/af/f61b58/00000000000000007735a84e/30/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n7&v=3") format("woff2"),url("https://use.typekit.net/af/f61b58/00000000000000007735a84e/30/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n7&v=3") format("woff"),url("https://use.typekit.net/af/f61b58/00000000000000007735a84e/30/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n7&v=3") format("opentype");
font-display:auto;font-style:normal;font-weight:700;font-stretch:normal;
}

@font-face {
font-family:"urw-din-semi-condensed";
src:url("https://use.typekit.net/af/349829/00000000000000007735a852/30/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i7&v=3") format("woff2"),url("https://use.typekit.net/af/349829/00000000000000007735a852/30/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i7&v=3") format("woff"),url("https://use.typekit.net/af/349829/00000000000000007735a852/30/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i7&v=3") format("opentype");
font-display:auto;font-style:italic;font-weight:700;font-stretch:normal;
}

@font-face {
font-family:"urw-din-semi-condensed";
src:url("https://use.typekit.net/af/8def5a/00000000000000007735a854/30/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n9&v=3") format("woff2"),url("https://use.typekit.net/af/8def5a/00000000000000007735a854/30/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n9&v=3") format("woff"),url("https://use.typekit.net/af/8def5a/00000000000000007735a854/30/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n9&v=3") format("opentype");
font-display:auto;font-style:normal;font-weight:900;font-stretch:normal;
}

@font-face {
font-family:"urw-din-semi-condensed";
src:url("https://use.typekit.net/af/d4ff8c/00000000000000007735a857/30/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i9&v=3") format("woff2"),url("https://use.typekit.net/af/d4ff8c/00000000000000007735a857/30/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i9&v=3") format("woff"),url("https://use.typekit.net/af/d4ff8c/00000000000000007735a857/30/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i9&v=3") format("opentype");
font-display:auto;font-style:italic;font-weight:900;font-stretch:normal;
}

.tk-urw-din { font-family: "urw-din",sans-serif; }
.tk-urw-din-condensed { font-family: "urw-din-condensed",sans-serif; }
.tk-urw-din-semi-condensed { font-family: "urw-din-semi-condensed",sans-serif; }
`;
